// When the homepage requests camera snapshots (Req 11.17, 12.3, 12.4). No DOM: timers and
// the refresh callback are injected (unit-tested in Node).
//
// - While the page is visible, every LIVE camera is refreshed once per Snapshot_Interval.
// - OFFLINE cameras are never requested (their placeholder is a static file).
// - While the page is hidden nothing is requested.
// - Becoming visible refreshes every LIVE camera at once (the caller also refetches the
//   camera status), then the interval resumes.
// - A camera that turns LIVE while visible is refreshed at once.
import type { RealtimeTimers } from '../lib/realtime.js';

export interface CameraSchedulerOptions {
  readonly timers: Pick<RealtimeTimers, 'setTimeout' | 'clearTimeout'>;
  /** Request a fresh snapshot of camera n. */
  readonly refresh: (n: number) => void;
  readonly intervalS: number;
  readonly visible: boolean;
}

export class CameraRefreshScheduler {
  private readonly timers: CameraSchedulerOptions['timers'];
  private readonly refresh: (n: number) => void;
  private intervalMs: number;
  private visibleValue: boolean;
  private readonly live = new Set<number>();
  private timer: unknown = null;
  private started = false;

  constructor(opts: CameraSchedulerOptions) {
    this.timers = opts.timers;
    this.refresh = opts.refresh;
    this.intervalMs = CameraRefreshScheduler.toMs(opts.intervalS);
    this.visibleValue = opts.visible;
  }

  private static toMs(s: number): number {
    return Number.isFinite(s) && s >= 1 ? Math.min(10, Math.round(s)) * 1000 : 2000;
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  get interval(): number {
    return this.intervalMs;
  }

  /** Cameras currently refreshed (LIVE). */
  liveCameras(): number[] {
    return [...this.live].sort((a, b) => a - b);
  }

  start(): void {
    this.started = true;
    this.schedule();
  }

  stop(): void {
    this.started = false;
    this.cancel();
  }

  setStatus(n: number, live: boolean): void {
    const was = this.live.has(n);
    if (live) this.live.add(n);
    else this.live.delete(n);
    if (live && !was && this.started && this.visibleValue) this.refresh(n);
  }

  setVisible(visible: boolean): void {
    if (visible === this.visibleValue) return;
    this.visibleValue = visible;
    this.cancel();
    if (!visible || !this.started) return;
    this.refreshAll();
    this.schedule();
  }

  setIntervalS(s: number): void {
    const ms = CameraRefreshScheduler.toMs(s);
    if (ms === this.intervalMs) return;
    this.intervalMs = ms;
    this.cancel();
    this.schedule();
  }

  private refreshAll(): void {
    for (const n of this.liveCameras()) this.refresh(n);
  }

  private cancel(): void {
    if (this.timer !== null) this.timers.clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    if (!this.started || !this.visibleValue || this.timer !== null) return;
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      if (!this.started || !this.visibleValue) return;
      this.refreshAll();
      this.schedule();
    }, this.intervalMs);
  }
}
